/**
 * A match as a sequence of periods, and what falls out of it.
 *
 * The coach plans the pieces — 0–30, 30–45, 45–70 — and names an eleven for
 * each. Everything else is arithmetic on that: who is on for how long, what
 * changes at each break, and which of them has been given no minutes at all.
 *
 * Pure and shared, because the screen shows these numbers while the coach is
 * still moving players around and the API returns them after it is saved. Two
 * implementations of "how many minutes did I give him" is one too many.
 */

export interface PlannedPeriod {
  fromMinute: number;
  toMinute: number;
  /** Player ids on the pitch for this period. */
  players: readonly string[];
}

export interface PlannedMinutes {
  playerId: string;
  minutes: number;
  /** How many separate spells, which is not the same as how many minutes. */
  spells: number;
}

export interface Substitution {
  atMinute: number;
  off: string[];
  on: string[];
}

/** Periods in the order they are played, whatever order they were entered in. */
export function inOrder<T extends { fromMinute: number }>(periods: readonly T[]): T[] {
  return [...periods].sort((a, b) => a.fromMinute - b.fromMinute);
}

export function periodLength(period: { fromMinute: number; toMinute: number }): number {
  return Math.max(0, period.toMinute - period.fromMinute);
}

/** Total minutes the plan covers, ignoring any overlap between periods. */
export function plannedLength(periods: readonly PlannedPeriod[]): number {
  return periods.reduce((total, p) => total + periodLength(p), 0);
}

/**
 * Minutes per player, highest first, then by the number of spells.
 *
 * Everyone named anywhere in the plan appears, including a player on for zero
 * minutes because his period has no length yet — leaving him out would hide the
 * mistake rather than show it.
 */
export function plannedMinutes(periods: readonly PlannedPeriod[]): PlannedMinutes[] {
  const total = new Map<string, { minutes: number; spells: number }>();
  for (const period of inOrder(periods)) {
    const length = periodLength(period);
    for (const playerId of period.players) {
      const line = total.get(playerId) ?? { minutes: 0, spells: 0 };
      line.minutes += length;
      line.spells += 1;
      total.set(playerId, line);
    }
  }
  return [...total.entries()]
    .map(([playerId, line]) => ({ playerId, ...line }))
    .sort((a, b) => b.minutes - a.minutes || a.spells - b.spells);
}

/**
 * What changes at each break.
 *
 * A player who is on either side of a break is not a substitution even if he
 * changes position, and a player who comes back on later is a new one — which is
 * the case a single on/off pair per player could never express.
 */
export function substitutions(periods: readonly PlannedPeriod[]): Substitution[] {
  const ordered = inOrder(periods);
  const changes: Substitution[] = [];
  for (let i = 1; i < ordered.length; i++) {
    const before = new Set(ordered[i - 1]!.players);
    const after = new Set(ordered[i]!.players);
    const off = [...before].filter((id) => !after.has(id));
    const on = [...after].filter((id) => !before.has(id));
    if (off.length || on.length) {
      changes.push({ atMinute: ordered[i]!.fromMinute, off, on });
    }
  }
  return changes;
}

export type PlanIssue =
  | { code: 'overlap'; atMinute: number }
  | { code: 'gap'; fromMinute: number; toMinute: number }
  | { code: 'backwards'; atMinute: number }
  | { code: 'size'; atMinute: number; count: number };

/**
 * What is wrong with the plan, named rather than judged.
 *
 * Returned as codes for the screen to put words to, and never as a refusal: a
 * coach sketching a friendly at midnight may well leave a gap between two
 * periods on the way to filling it, and an app that will not hold a half-finished
 * thought is an app that gets left on the desk.
 */
export function planIssues(
  periods: readonly PlannedPeriod[],
  options: { onPitch?: number } = {},
): PlanIssue[] {
  const onPitch = options.onPitch ?? 11;
  const ordered = inOrder(periods);
  const issues: PlanIssue[] = [];

  ordered.forEach((period, i) => {
    if (period.toMinute <= period.fromMinute) {
      issues.push({ code: 'backwards', atMinute: period.fromMinute });
    }
    if (period.players.length !== onPitch) {
      issues.push({
        code: 'size',
        atMinute: period.fromMinute,
        count: period.players.length,
      });
    }
    const previous = ordered[i - 1];
    if (!previous) return;
    if (period.fromMinute < previous.toMinute) {
      issues.push({ code: 'overlap', atMinute: period.fromMinute });
    } else if (period.fromMinute > previous.toMinute) {
      issues.push({
        code: 'gap',
        fromMinute: previous.toMinute,
        toMinute: period.fromMinute,
      });
    }
  });

  return issues;
}

/** Players in the squad who the plan never puts on. Friendlies exist for these. */
export function unusedPlayers(
  periods: readonly PlannedPeriod[],
  squad: readonly string[],
): string[] {
  const used = new Set(periods.flatMap((p) => p.players));
  return squad.filter((id) => !used.has(id));
}
